import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type UnsavedBrowserNavigationGuardOptions = {
  enabled: boolean;
};

type Jsonish = null | boolean | number | string | Jsonish[] | { [key: string]: Jsonish };

const VOLATILE_FORM_KEYS = new Set(['id']);

const isFileLike = (value: unknown): value is File => (
  typeof File !== 'undefined' && value instanceof File
);

const normalizeUnsavedFormValue = (value: unknown): Jsonish => {
  if (value == null) return null;
  if (isFileLike(value)) {
    return {
      name: value.name,
      size: value.size,
      type: value.type,
      lastModified: value.lastModified,
    };
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (Array.isArray(value)) {
    return value.map(normalizeUnsavedFormValue);
  }
  if (typeof value === 'object') {
    const normalizedEntries = Object.entries(value as Record<string, unknown>)
      .filter(([key]) => !VOLATILE_FORM_KEYS.has(key))
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entryValue]) => [key, normalizeUnsavedFormValue(entryValue)] as const);
    return Object.fromEntries(normalizedEntries) as { [key: string]: Jsonish };
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'boolean' || typeof value === 'string') {
    return value;
  }
  return String(value);
};

const serializeFormValue = (value: unknown): string => JSON.stringify(normalizeUnsavedFormValue(value));

export const hasUnsavedFormData = (current: unknown, initial: unknown): boolean => (
  serializeFormValue(current) !== serializeFormValue(initial)
);

export const useUnsavedBrowserNavigationGuard = ({
  enabled,
}: UnsavedBrowserNavigationGuardOptions) => {
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const confirmedNavigationRef = useRef(false);
  const promptOpenRef = useRef(false);
  const guardId = useMemo(
    () => `ystnj-unsaved-form-${Math.random().toString(36).slice(2)}`,
    []
  );

  useEffect(() => {
    promptOpenRef.current = isPromptOpen;
  }, [isPromptOpen]);

  useEffect(() => {
    confirmedNavigationRef.current = false;
    if (!enabled) {
      setIsPromptOpen(false);
      return undefined;
    }
    if (typeof window === 'undefined') {
      return undefined;
    }

    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const existingState = (
      window.history.state && typeof window.history.state === 'object'
        ? window.history.state as Record<string, unknown>
        : {}
    );

    try {
      window.history.pushState(
        { ...existingState, __ystnjUnsavedFormGuard: guardId },
        '',
        currentUrl
      );
    } catch {
      // Browser history can be unavailable in tests or embedded previews.
    }

    const handlePopState = () => {
      if (confirmedNavigationRef.current) {
        return;
      }
      if (promptOpenRef.current) {
        window.setTimeout(() => window.history.forward(), 0);
        return;
      }
      setIsPromptOpen(true);
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, guardId]);

  const cancelNavigation = useCallback(() => {
    setIsPromptOpen(false);
    if (typeof window === 'undefined') return;
    window.setTimeout(() => window.history.forward(), 0);
  }, []);

  const confirmNavigation = useCallback(() => {
    confirmedNavigationRef.current = true;
    setIsPromptOpen(false);
    if (typeof window === 'undefined') return;
    window.history.back();
  }, []);

  return {
    isPromptOpen,
    cancelNavigation,
    confirmNavigation,
  };
};
